import {useMemo, useEffect} from 'react';
import { useLocation } from 'react-router-dom';
import usePageData from './usePageData';
import extractDataFromJson from './extractDataFromJson';
import LoadImages from './LoadImages';
import Footer from '../../Footer/Footer';
import LoadingScreen from './LoadingScreen';

/**
 * Reusable wrapper that handles loading, image preloading, and layout for page content.
 *
 * @param {Object} props
 * @param {string} props.pageName - The name of the page file to fetch data from.
 * @param {function} props.render - A render function that receives { data, sections, sectionNames }.
 * @returns {JSX.Element}
 */
export default function PageState({ pageName, render }) {
  const { data } = usePageData(pageName);
  const location = useLocation();

  const { images, sections, sectionNames } = useMemo(
    () => extractDataFromJson(data),
    [data] 
  );

  // Scroll to hash AFTER content with IDs is in the DOM
  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.slice(1);
    let tries = 0;
    const maxTries = 12;

    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        // use 'instant' (or 'auto') on initial load to avoid fighting any top scroll
        el.scrollIntoView({ behavior: 'instant', block: 'start' });
        return;
      }
      if (tries++ < maxTries) requestAnimationFrame(attempt);
    };

    attempt();
  }, [location.pathname, location.hash, sections.length]);

  if (!data) return <LoadingScreen/>;

  return (
    <>
      <LoadImages images={images} />
      {render({ data, sections, sectionNames })}
      <Footer />
    </>
  );
}
